import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import ProjectIcon from '../components/ProjectIcon';
import ProjectCard from '../components/ProjectCard';
import { getService, services } from '../data/services';
import { projects } from '../data/projects';
import './Projects.css';
import './ProjectDetail.css';
import './Home.css';
import './Blog.css';

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = getService(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <div className="page-shell">
        <div className="container page-hero">
          <h1 className="page-title">Service not found</h1>
          <p className="page-subtitle">That service doesn't exist or has been renamed.</p>
          <Link to="/services" className="btn btn-primary">Back to services</Link>
        </div>
      </div>
    );
  }

  const others = services.filter((s) => s.slug !== service.slug).slice(0, 3);
  const relatedProjects = projects.filter((p) => service.relatedProducts.includes(p.slug));

  return (
    <>
      <title>{`${service.title} - Pidesk`}</title>
      <meta name="description" content={service.tagline} />

      <div className="detail-page">
        <div className="container detail-wrap">
          <Link to="/services" className="back-link"><ArrowLeft size={16} /> All services</Link>

          <header className={`detail-hero tone-${service.tone}`}>
            <div className="detail-hero-inner">
              <div className="detail-icon"><ProjectIcon name={service.icon} size={40} /></div>
              <div className="detail-hero-text">
                <div className="detail-pills">
                  <span className="dpill">Service</span>
                </div>
                <h1 className="detail-name">{service.title}</h1>
                <p className="detail-tagline">{service.tagline}</p>

                <div className="detail-actions">
                  <Link className="btn btn-primary btn-lg" to="/contact">Start a project</Link>
                  <Link className="btn btn-secondary btn-lg" to="/pricing">View pricing</Link>
                </div>
              </div>
            </div>
          </header>

          <div className="detail-layout">
            <div className="detail-main">
              <section className="detail-section">
                <h2>About this service</h2>
                <p className="detail-about">{service.description}</p>
              </section>

              <section className="detail-section">
                <h2>What's included</h2>
                <ul className="feature-list">
                  {service.features.map((f) => (
                    <li key={f.title} style={{ alignItems: 'flex-start', flexDirection: 'column', gap: 4 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span className="feature-check"><Check size={14} /></span> {f.title}
                      </span>
                      <span style={{ marginLeft: 34, color: 'var(--text-secondary)', fontWeight: 400, fontSize: '0.9rem' }}>{f.desc}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="detail-section">
                <h2>How we work</h2>
                <ol className="process-grid" style={{ listStyle: 'none' }}>
                  {service.process.map((step, i) => (
                    <li key={step.title} className="process-step">
                      <span className="process-num">{String(i + 1).padStart(2, '0')}</span>
                      <h3>{step.title}</h3>
                      <p>{step.desc}</p>
                    </li>
                  ))}
                </ol>
              </section>

              <section className="detail-section">
                <h2>Frequently asked questions</h2>
                {service.faqs.map((f) => (
                  <details key={f.q} className="faq-item">
                    <summary>{f.q}</summary>
                    <p>{f.a}</p>
                  </details>
                ))}
              </section>
            </div>

            <aside className="detail-side">
              <div className="side-card">
                <h3>Built with</h3>
                <div className="tech-tags">
                  {service.tech.map((t) => <span key={t}>{t}</span>)}
                </div>
              </div>

              <div className="side-cta">
                <h3>Ready to start?</h3>
                <p>Tell us about your project and we'll come back with a plan and estimate.</p>
                <Link to="/contact" className="btn btn-primary">Start a project <ArrowRight size={16} style={{ marginLeft: 8 }} /></Link>
              </div>
            </aside>
          </div>

          {relatedProjects.length > 0 && (
            <section className="detail-section detail-more">
              <h2>Projects built with this service</h2>
              <div className="projects-grid">
                {relatedProjects.map((p) => (
                  <ProjectCard key={p.slug} project={p} index={projects.findIndex((x) => x.slug === p.slug)} />
                ))}
              </div>
            </section>
          )}

          {others.length > 0 && (
            <section className="detail-section">
              <h2>Other services</h2>
              <div className="projects-grid">
                {others.map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}`} className={`project-card tone-${s.tone}`}>
                    <span className="project-open"><ArrowRight size={16} /></span>
                    <div className="pc-text">
                      <span className="project-category">Service</span>
                      <h3 className="project-name">{s.title}</h3>
                      <p className="project-desc">{s.tagline}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export default ServiceDetail;
